import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { 
  PenLine, 
  Upload, 
  CheckCircle2, 
  ArrowLeft,
  MapPin,
  Sprout,
  User,
  FileText,
  Loader2,
  Image as ImageIcon,
  Save
} from 'lucide-react';

var API_URL = process.env.REACT_APP_BACKEND_URL;

var themes = [
  { id: 'land', name: 'Land', icon: '🌍', description: 'Access to land and ownership' },
  { id: 'finance', name: 'Finance', icon: '💰', description: 'Funding, loans, and investment' },
  { id: 'inputs', name: 'Inputs', icon: '🌱', description: 'Seeds, fertilizers, and tools' },
  { id: 'markets', name: 'Markets', icon: '🏪', description: 'Selling and market access' },
  { id: 'climate', name: 'Climate', icon: '☀️', description: 'Weather and climate adaptation' },
  { id: 'skills', name: 'Skills', icon: '📚', description: 'Training and knowledge' },
];

var countries = [
  { id: 'tanzania', name: 'Tanzania', flag: '🇹🇿' },
  { id: 'ghana', name: 'Ghana', flag: '🇬🇭' },
  { id: 'malawi', name: 'Malawi', flag: '🇲🇼' },
  { id: 'rwanda', name: 'Rwanda', flag: '🇷🇼' },
  { id: 'mali', name: 'Mali', flag: '🇲🇱' },
  { id: 'burkina-faso', name: 'Burkina Faso', flag: '🇧🇫' },
  { id: 'mozambique', name: 'Mozambique', flag: '🇲🇿' },
];

export default function SubmitStoryPage() {
  var navigate = useNavigate();
  var [searchParams] = useSearchParams();
  var editStoryId = searchParams.get('edit');
  var auth = useAuth();
  var [submitted, setSubmitted] = useState(false);
  var [loading, setLoading] = useState(false);
  var [uploading, setUploading] = useState(false);
  var [error, setError] = useState('');
  var [editLoading, setEditLoading] = useState(!!editStoryId);
  var [editNotAllowed, setEditNotAllowed] = useState(false);
  var [showTerms, setShowTerms] = useState(false);
  var [termsAccepted, setTermsAccepted] = useState(false);
  var [ageConfirmed, setAgeConfirmed] = useState(false);
  var fileInputRef = useRef(null);
  
  var [formData, setFormData] = useState({
    title: '',
    caption: '',
    full_content: '',
    country_id: '',
    theme: '',
    author_name: auth.user ? auth.user.name : '',
    image_url: '',
  });

  // If editing, fetch the existing story
  useEffect(function() {
    if (editStoryId && auth.isAuthenticated && auth.token) {
      fetch(API_URL + '/api/contributor/my-stories/' + editStoryId, {
        headers: { 'Authorization': 'Bearer ' + auth.token }
      })
        .then(function(r) {
          if (!r.ok) throw new Error('Story not found');
          return r.json();
        })
        .then(function(story) {
          // Check if editable
          if (story.status !== 'incoming' && story.status !== 'needs_revision') {
            setEditNotAllowed(true);
            setEditLoading(false);
            return;
          }
          setFormData({
            title: story.title || '',
            caption: story.caption || '',
            full_content: story.full_content || '',
            country_id: story.country_id || '',
            theme: story.theme || '',
            author_name: story.author_name || '',
            image_url: story.image_url || '',
          });
          setEditLoading(false);
        })
        .catch(function() {
          setError('Could not load story for editing.');
          setEditLoading(false);
        });
    } else if (editStoryId && !auth.isAuthenticated) {
      navigate('/login');
    } else {
      setEditLoading(false);
    }
  }, [editStoryId, auth.isAuthenticated, auth.token]);

  var handleChange = function(field, value) {
    setFormData(function(prev) { return Object.assign({}, prev, { [field]: value }); });
    setError('');
  };

  var handleFileUpload = async function(e) {
    var file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    setError('');
    
    try {
      var uploadData = new FormData();
      uploadData.append('file', file);
      uploadData.append('folder', 'stories');
      
      var response = await fetch(API_URL + '/api/uploads/image', {
        method: 'POST',
        body: uploadData
      });
      
      if (response.ok) {
        var data = await response.json();
        var imageUrl = data.url.startsWith('/') ? API_URL + data.url : data.url;
        setFormData(function(prev) { return Object.assign({}, prev, { image_url: imageUrl }); });
      } else {
        var errData = await response.json();
        setError(errData.detail || 'Failed to upload image');
      }
    } catch (err) {
      setError('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  var handleSubmit = async function(e) {
    e.preventDefault();
    setError('');

    if (!formData.title || !formData.caption || !formData.country_id || !formData.theme || !formData.author_name) {
      setError('Please fill in all required fields');
      return;
    }

    if (editStoryId) {
      // Editing — skip terms, submit directly
      await doSubmit();
    } else {
      // New submission — show terms first
      setShowTerms(true);
    }
  };

  var doSubmit = async function() {
    setLoading(true);
    setError('');

    try {
      var selectedCountry = countries.find(function(c) { return c.id === formData.country_id; });
      
      if (editStoryId) {
        // PATCH update existing story
        var patchResp = await fetch(API_URL + '/api/contributor/my-stories/' + editStoryId, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + auth.token
          },
          body: JSON.stringify({
            title: formData.title,
            caption: formData.caption,
            full_content: formData.full_content,
            theme: formData.theme,
            image_url: formData.image_url,
          })
        });

        if (patchResp.ok) {
          setSubmitted(true);
        } else {
          var patchErr = await patchResp.json();
          setError(patchErr.detail || 'Failed to update story');
        }
      } else {
        // POST new story with terms acceptance
        var endpoint = auth.isAuthenticated ? '/api/stories/submit' : '/api/stories/submit-public';
        var headers = { 'Content-Type': 'application/json' };
        if (auth.isAuthenticated) {
          headers['Authorization'] = 'Bearer ' + auth.token;
        }
        
        var postResp = await fetch(API_URL + endpoint, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify(Object.assign({}, formData, {
            country_name: selectedCountry ? selectedCountry.name : formData.country_id,
            author_country: selectedCountry ? selectedCountry.name : formData.country_id,
            terms_accepted: true,
            terms_accepted_at: new Date().toISOString(),
          }))
        });

        if (postResp.ok) {
          setSubmitted(true);
        } else {
          var postErr = await postResp.json();
          setError(postErr.detail || 'Failed to submit story');
        }
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  var isEditing = !!editStoryId;

  // Edit not allowed screen
  if (editNotAllowed) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-20">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-md mx-auto">
            <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-3">Cannot Edit This Story</h1>
            <p className="text-gray-600 mb-6">This story has already been approved or published and can no longer be edited.</p>
            <Button onClick={function() { navigate('/contributor'); }} className="bg-[#07743F] hover:bg-[#065a32] text-white">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Loading edit data
  if (editLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#07743F]" />
      </div>
    );
  }

  // Terms acceptance screen
  if (showTerms && !submitted) {
    var canSubmit = termsAccepted && ageConfirmed;
    return (
      <div className="min-h-screen" style={{ backgroundColor: '#F9F8F6' }}>
        <div className="bg-white border-b border-gray-100 py-3">
          <div className="container mx-auto px-4">
            <Button variant="ghost" onClick={function() { setShowTerms(false); }} className="text-[#07743F] hover:bg-green-50">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to story
            </Button>
          </div>
        </div>

        <section className="py-12">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto">
              <div className="text-center mb-8">
                <h1 className="text-3xl font-serif text-gray-900 mb-3" data-testid="terms-title">Almost there</h1>
                <p className="text-gray-500">Before you share your story, we just need your permission to use it responsibly.</p>
              </div>

              <div className="bg-white border border-gray-200 rounded-2xl overflow-hidden mb-8" data-testid="terms-container">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
                  <h2 className="font-semibold text-gray-900 text-sm">Terms of Use & Content Release</h2>
                </div>
                <div className="px-6 py-5 max-h-[400px] overflow-y-auto text-sm text-gray-600 leading-relaxed space-y-4" data-testid="terms-content">
                  <p className="font-semibold text-gray-900 text-base">GROW YOUR TOMORROW</p>
                  <p className="font-medium text-gray-800">Contributor Terms of Use & Content Release Agreement</p>
                  <p className="italic">Effective Date: The date of your acceptance of these terms</p>
                  <p>Grow Your Tomorrow is an initiative by YEFFA, implemented in partnership with AGRA and the Mastercard Foundation.</p>
                  <p>By submitting any content (including videos, photographs, audio, written stories, or other materials), you agree to the following terms.</p>

                  <p className="font-semibold text-gray-800 pt-2">1. Purpose of the Platform</p>
                  <p>Grow Your Tomorrow is a storytelling and engagement platform designed to:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Share real experiences from young people in agriculture and agribusiness</li>
                    <li>Amplify youth voices across multiple countries</li>
                    <li>Inform programmes, partnerships, and policy discussions</li>
                  </ul>

                  <p className="font-semibold text-gray-800 pt-2">2. Your Content</p>
                  <p>By submitting content ("Your Content"), you confirm that:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>You are the original creator of the content, or have permission to share it</li>
                    <li>The content does not violate any laws or rights of others</li>
                    <li>Any individuals appearing in your content have given their consent</li>
                  </ul>

                  <p className="font-semibold text-gray-800 pt-2">3. Grant of Rights</p>
                  <p>By submitting Your Content, you grant YEFFA, AGRA, the Mastercard Foundation, and their authorised partners, a non-exclusive, royalty-free, worldwide, perpetual licence to:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>Use, reproduce, publish, and share Your Content</li>
                    <li>Adapt, edit, translate, and format Your Content</li>
                    <li>Distribute Your Content across digital platforms, broadcast media, print materials, and events</li>
                    <li>Use Your Content for communication campaigns, research, programme development, reporting, and policy engagement</li>
                  </ul>
                  <p>Your Content may be used with or without attribution unless otherwise agreed.</p>

                  <p className="font-semibold text-gray-800 pt-2">4. No Compensation</p>
                  <p>You understand and agree that:</p>
                  <ul className="list-disc pl-5 space-y-1">
                    <li>You will not receive payment for the use of Your Content</li>
                    <li>Your participation is voluntary</li>
                    <li>Your contribution supports a broader initiative to amplify youth voices and drive impact</li>
                  </ul>

                  <p className="font-semibold text-gray-800 pt-2">5. Editing and Adaptation</p>
                  <p>You agree that Your Content may be edited for clarity, length, translation, or format. It may be adapted into different formats and combined with other content. All such use will aim to preserve the original meaning and intent of your story.</p>

                  <p className="font-semibold text-gray-800 pt-2">6. Data and Privacy</p>
                  <p>We may collect and use basic information you provide (name, location, contact details) to attribute your story, contact you regarding your submission, and support programme insights. Your data will be handled responsibly and will not be sold to third parties.</p>

                  <p className="font-semibold text-gray-800 pt-2">7. Withdrawal of Content</p>
                  <p>You may request that your content be removed from future use by contacting us. Please note that content already published or distributed may not be fully retractable.</p>

                  <p className="font-semibold text-gray-800 pt-2">8. Eligibility</p>
                  <p>By submitting content, you confirm that you are at least 18 years old, or have consent from a parent or guardian.</p>

                  <p className="font-semibold text-gray-800 pt-2">9. Acceptance</p>
                  <p>By selecting "I Agree" or submitting content, you confirm that you have read and understood these terms and agree to all conditions outlined above.</p>

                  <p className="font-semibold text-gray-800 pt-2">10. Contact</p>
                  <p>For any questions regarding these terms, please contact the Grow Your Tomorrow team.</p>

                  <p className="italic pt-4 text-gray-500 border-t border-gray-100 mt-4">This platform exists to ensure that your voice contributes to something larger. Your story does not end here. It travels.</p>
                </div>
              </div>

              <div className="space-y-4 mb-8">
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="terms-checkbox">
                  <input
                    type="checkbox"
                    checked={termsAccepted}
                    onChange={function(e) { setTermsAccepted(e.target.checked); }}
                    className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#07743F] focus:ring-[#07743F]"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    I have read and agree to the <strong>Terms of Use & Content Release</strong>
                  </span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="age-checkbox">
                  <input
                    type="checkbox"
                    checked={ageConfirmed}
                    onChange={function(e) { setAgeConfirmed(e.target.checked); }}
                    className="mt-0.5 w-5 h-5 rounded border-gray-300 text-[#07743F] focus:ring-[#07743F]"
                  />
                  <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                    I confirm I am 18 years or older, or have guardian consent
                  </span>
                </label>
              </div>

              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm mb-4" data-testid="terms-error">{error}</div>
              )}

              <Button
                onClick={doSubmit}
                disabled={!canSubmit || loading}
                className={'w-full py-6 text-lg font-semibold rounded-full transition-all ' + (canSubmit ? 'bg-[#07743F] hover:bg-[#065a32] text-white cta-alive' : 'bg-gray-200 text-gray-400 cursor-not-allowed')}
                data-testid="terms-submit-btn"
              >
                {loading ? (
                  <span className="flex items-center justify-center"><Loader2 className="w-5 h-5 mr-2 animate-spin" />Sharing...</span>
                ) : (
                  <span className="flex items-center justify-center"><PenLine className="w-5 h-5 mr-2" />Share My Story</span>
                )}
              </Button>

              {!canSubmit && (
                <p className="text-center text-sm text-gray-400 mt-3">Please accept the terms to continue</p>
              )}
            </div>
          </div>
        </section>
      </div>
    );
  }

  // Success screen
  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-green-50 to-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-xl mx-auto text-center">
            <div className="w-24 h-24 bg-[#72BF44] rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce-in">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4 animate-fade-in-up">
              {isEditing ? 'Story Updated!' : 'Story Received!'}
            </h1>
            <p className="text-xl text-gray-600 mb-2 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              {isEditing ? 'Your changes have been saved.' : 'Thank you for sharing your voice.'}
            </p>
            <p className="text-lg text-gray-500 mb-8 animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Your story is now <strong>under review</strong>. Our team will verify and approve it for publication.
            </p>
            
            {!isEditing && (
              <div className="bg-white border-2 border-[#72BF44] rounded-xl p-6 mb-8 animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
                <h3 className="font-bold text-gray-900 mb-3">What happens next?</h3>
                <div className="text-left space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#07743F] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</div>
                    <p className="text-gray-600">Our team reviews your story for safety and accuracy</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#07743F] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</div>
                    <p className="text-gray-600">Once approved, your story goes live</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 bg-[#07743F] text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</div>
                    <p className="text-gray-600">Your story may be featured across our channels</p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex gap-4 justify-center animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              {auth.isAuthenticated ? (
                <Button onClick={function() { navigate('/contributor'); }} className="bg-[#07743F] hover:bg-[#065a32] text-white px-6">
                  My Dashboard
                </Button>
              ) : (
                <Button onClick={function() { navigate('/'); }} className="bg-[#07743F] hover:bg-[#065a32] text-white px-6">
                  Back to Home
                </Button>
              )}
              {!isEditing && (
                <Button
                  onClick={function() { setSubmitted(false); setFormData({ title: '', caption: '', full_content: '', country_id: '', theme: '', author_name: auth.user ? auth.user.name : '', image_url: '' }); }}
                  variant="outline"
                  className="border-[#07743F] text-[#07743F]"
                >
                  Submit Another
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-green-200 py-3">
        <div className="container mx-auto px-4">
          <Button
            variant="ghost"
            onClick={function() { navigate(isEditing ? '/contributor' : '/'); }}
            className="text-[#07743F] hover:bg-green-50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isEditing ? 'Back to Dashboard' : 'Back to Home'}
          </Button>
        </div>
      </div>

      {/* Header */}
      <section className="py-12 bg-gradient-to-r from-[#07743F] to-[#065a32]">
        <div className="container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-full mb-6">
            {isEditing ? <Save className="w-8 h-8 text-white" /> : <PenLine className="w-8 h-8 text-white" />}
          </div>
          <h1 className="text-4xl font-serif text-white mb-4" data-testid="submit-page-title">
            {isEditing ? 'Edit Your Story' : 'Tell Your Story'}
          </h1>
          <p className="text-green-100 text-lg max-w-2xl mx-auto">
            {isEditing ? 'Update the details of your story below.' : 'Your voice matters. Share what you are building, learning, or dreaming. It takes less than 2 minutes.'}
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto border-2 border-green-200">
            <CardContent className="p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Author Name */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <User className="w-4 h-4" />
                    Your Name *
                  </Label>
                  <Input
                    type="text"
                    placeholder="Enter your name"
                    value={formData.author_name}
                    onChange={function(e) { handleChange('author_name', e.target.value); }}
                    className="border-gray-300 focus:border-[#72BF44]"
                    data-testid="submit-author-name"
                    disabled={isEditing}
                  />
                </div>

                {/* Title */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <FileText className="w-4 h-4" />
                    Story Title *
                  </Label>
                  <Input
                    type="text"
                    placeholder="Give your story a title"
                    value={formData.title}
                    onChange={function(e) { handleChange('title', e.target.value); }}
                    className="border-gray-300 focus:border-[#72BF44]"
                    data-testid="submit-title"
                  />
                </div>

                {/* Caption */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <PenLine className="w-4 h-4" />
                    Short Caption *
                  </Label>
                  <textarea
                    placeholder="Write a brief summary of your story (1-2 sentences)"
                    value={formData.caption}
                    onChange={function(e) { handleChange('caption', e.target.value); }}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#72BF44] focus:border-transparent"
                    data-testid="submit-caption"
                  />
                </div>

                {/* Full Content */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <FileText className="w-4 h-4" />
                    Full Story (Optional)
                  </Label>
                  <textarea
                    placeholder="Tell your complete story..."
                    value={formData.full_content}
                    onChange={function(e) { handleChange('full_content', e.target.value); }}
                    rows={6}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#72BF44] focus:border-transparent"
                    data-testid="submit-content"
                  />
                </div>

                {/* Country */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <MapPin className="w-4 h-4" />
                    Your Country *
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {countries.map(function(country) {
                      var isSelected = formData.country_id === country.id;
                      return (
                        <button
                          key={country.id}
                          type="button"
                          onClick={function() { handleChange('country_id', country.id); }}
                          className={'p-3 rounded-lg border-2 text-center transition-all ' + (isSelected ? 'border-[#07743F] bg-green-50' : 'border-gray-200 hover:border-gray-300')}
                          data-testid={'submit-country-' + country.id}
                          disabled={isEditing}
                        >
                          <span className="text-2xl block mb-1">{country.flag}</span>
                          <span className="text-xs font-medium text-gray-700">{country.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Theme */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <Sprout className="w-4 h-4" />
                    Story Theme *
                  </Label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {themes.map(function(theme) {
                      var isSelected = formData.theme === theme.id;
                      return (
                        <button
                          key={theme.id}
                          type="button"
                          onClick={function() { handleChange('theme', theme.id); }}
                          className={'p-3 rounded-lg border-2 text-left transition-all ' + (isSelected ? 'border-[#07743F] bg-green-50' : 'border-gray-200 hover:border-gray-300')}
                          data-testid={'submit-theme-' + theme.id}
                        >
                          <span className="text-xl block mb-1">{theme.icon}</span>
                          <span className="text-sm font-medium text-gray-900 block">{theme.name}</span>
                          <span className="text-xs text-gray-500">{theme.description}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Image Upload */}
                <div>
                  <Label className="flex items-center gap-2 text-gray-700 mb-2">
                    <Upload className="w-4 h-4" />
                    Story Image
                  </Label>
                  
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileUpload}
                    accept="image/*"
                    className="hidden"
                  />
                  
                  {formData.image_url ? (
                    <div className="relative">
                      <img 
                        src={formData.image_url} 
                        alt="Preview" 
                        className="w-full h-48 object-cover rounded-lg border-2 border-green-300"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={function() { setFormData(function(prev) { return Object.assign({}, prev, { image_url: '' }); }); }}
                        className="absolute top-2 right-2 bg-white"
                      >
                        Remove
                      </Button>
                    </div>
                  ) : (
                    <div 
                      onClick={function() { if (fileInputRef.current) fileInputRef.current.click(); }}
                      className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-[#72BF44] transition-colors"
                    >
                      {uploading ? (
                        <div className="flex flex-col items-center">
                          <Loader2 className="w-8 h-8 animate-spin text-[#07743F] mb-2" />
                          <p className="text-sm text-gray-600">Uploading...</p>
                        </div>
                      ) : (
                        <div>
                          <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                          <p className="text-sm text-gray-600 mb-1">Click to upload an image</p>
                          <p className="text-xs text-gray-400">JPG, PNG, GIF up to 10MB</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="mt-3">
                    <p className="text-xs text-gray-500 mb-1">Or paste an image URL:</p>
                    <Input
                      type="url"
                      placeholder="https://example.com/image.jpg"
                      value={formData.image_url}
                      onChange={function(e) { handleChange('image_url', e.target.value); }}
                      className="border-gray-300 focus:border-[#72BF44] text-sm"
                      data-testid="submit-image-url"
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="bg-red-50 text-red-700 p-3 rounded-lg text-sm" data-testid="submit-error">
                    {error}
                  </div>
                )}

                {/* Submit Button */}
                <Button 
                  type="submit"
                  disabled={loading}
                  className="w-full bg-[#07743F] hover:bg-[#065a32] text-white py-6 text-lg font-semibold"
                  data-testid="submit-story-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      {isEditing ? 'Saving...' : 'Submitting...'}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      {isEditing ? <Save className="w-5 h-5 mr-2" /> : <PenLine className="w-5 h-5 mr-2" />}
                      {isEditing ? 'Save Changes' : 'Share My Story'}
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
}
